import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Upload, X } from "lucide-react";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAuth } from "../context/AuthContext";
import { buildTestResultsSchema } from "../schemas/testResults";
import type { TestCase, TestResult, Wallet } from "../types";
import FieldError from "./FieldError";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { DatePicker } from "./ui/date-picker";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import ValidationBanner from "./ValidationBanner";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
	"image/png",
	"image/jpeg",
	"image/jpg",
	"application/pdf",
];

interface TestResultsFormProps {
	testCase: TestCase;
	wallet: Wallet;
	existingResult?: TestResult;
	isSubmitting?: boolean;
	onSubmit: (result: TestResult) => void;
	onCancel: () => void;
}

export const TestResultsForm: React.FC<TestResultsFormProps> = ({
	testCase,
	wallet,
	existingResult,
	isSubmitting = false,
	onSubmit,
	onCancel,
}) => {
	const { user } = useAuth();
	const [isDragging, setIsDragging] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const bannerRef = useRef<HTMLDivElement>(null);

	const schema = useMemo(() => buildTestResultsSchema(testCase), [testCase]);

	const form = useForm({
		resolver: zodResolver(schema),
		defaultValues: {
			referenceNumber: existingResult?.reference_number || "",
			dateOfBirth: existingResult?.date_of_birth || "",
			transactionStatus: existingResult?.transaction_status || "",
			amount: existingResult?.amount || "",
			fees: existingResult?.fees || "",
			amountReceived: existingResult?.amount_received || "",
			transactionId: existingResult?.transaction_id || "",
			notes: existingResult?.notes || "",
			screenshotUrl: existingResult?.screenshot_url || "",
		},
		mode: "onSubmit",
	});

	const handleFileUpload = (file: File, onChange: (url: string) => void) => {
		if (!file) return;

		setUploadError(null);

		if (file.size > MAX_FILE_SIZE) {
			setUploadError("File size must be 10MB or less");
			return;
		}
		if (!ALLOWED_FILE_TYPES.includes(file.type)) {
			setUploadError("File must be PNG, JPG, or PDF");
			return;
		}

		const reader = new FileReader();
		reader.onload = () => {
			onChange(reader.result as string);
		};
		reader.readAsDataURL(file);
	};

	const handleDrop = (
		e: React.DragEvent<HTMLDivElement>,
		onChange: (url: string) => void,
	) => {
		e.preventDefault();
		setIsDragging(false);

		const file = e.dataTransfer.files?.[0];
		if (file) {
			handleFileUpload(file, onChange);
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = () => {
		setIsDragging(false);
	};

	const onValidSubmit = (data: Record<string, unknown>) => {
		const result: TestResult = {
			id: existingResult?.id || `test-result-${Date.now()}`,
			wallet_id: wallet.id,
			test_case_id: testCase.id,
			test_case_name: testCase.name,
			status: "submitted",
			reference_number: String(data.referenceNumber ?? ""),
			date_of_birth: String(data.dateOfBirth ?? ""),
			transaction_status: String(data.transactionStatus ?? ""),
			amount: String(data.amount ?? ""),
			fees: String(data.fees ?? ""),
			amount_received: String(data.amountReceived ?? ""),
			transaction_id: String(data.transactionId ?? ""),
			notes: String(data.notes ?? ""),
			screenshot_url: String(data.screenshotUrl ?? ""),
			submitted_by: user?.id || "",
			submitted_at: existingResult?.submitted_at || new Date().toISOString(),
		};

		onSubmit(result);
	};

	const onInvalid = () => {
		bannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const errors = form.formState.errors;
	const hasErrors = Object.keys(errors).length > 0;
	const errorMessages = hasErrors
		? Object.values(errors)
				.filter((e): e is { message?: string } => !!e && typeof e === "object")
				.map((e) => e.message)
				.filter((m): m is string => !!m)
		: [];
	const submitting = form.formState.isSubmitting;

	const RequiredIndicator = () => (
		<span className="ml-0.5 text-(--color-error-500)">*</span>
	);

	return (
		<div>
			<Button type="button" variant="ghost" onClick={onCancel} className="mb-4">
				<ArrowLeft size={18} />
				Back to Instructions
			</Button>

			<form
				onSubmit={form.handleSubmit(onValidSubmit, onInvalid)}
				className="space-y-5"
			>
				<Card className="border p-5">
					<h2 className="mt-0 mb-5 text-2xl">
						{existingResult ? "Edit" : "Submit"} Test Results: {testCase.name}
					</h2>

					<div ref={bannerRef}>
						{hasErrors && (
							<ValidationBanner
								message="Please fix the errors below before submitting."
								fields={errorMessages}
								aria-live="polite"
							/>
						)}
					</div>

					{/* Reference Number */}
					{testCase.requiredFields.referenceNumber && (
						<div className="space-y-1.5">
							<Label htmlFor="test-reference-number">
								MoneyGram Reference Number
								<RequiredIndicator />
							</Label>
							<Input
								id="test-reference-number"
								type="text"
								{...form.register("referenceNumber")}
								placeholder="Enter reference number"
								className={
									errors.referenceNumber
										? "border-(--color-error-500)"
										: undefined
								}
								aria-invalid={!!errors.referenceNumber}
								aria-describedby={
									errors.referenceNumber
										? "test-reference-number-error"
										: undefined
								}
							/>
							{errors.referenceNumber?.message && (
								<FieldError
									id="test-reference-number-error"
									message={String(errors.referenceNumber.message)}
								/>
							)}
						</div>
					)}

					{/* Transaction Status */}
					{testCase.requiredFields.transactionStatus && (
						<div className="space-y-1.5">
							<Label htmlFor="test-transaction-status">
								Transaction Status
								<RequiredIndicator />
							</Label>
							<Controller
								control={form.control}
								name="transactionStatus"
								render={({ field }) => (
									<Select
										value={String(field.value ?? "")}
										onValueChange={field.onChange}
									>
										<SelectTrigger
											id="test-transaction-status"
											className={
												errors.transactionStatus
													? "border-(--color-error-500)"
													: undefined
											}
											aria-invalid={!!errors.transactionStatus}
											aria-describedby={
												errors.transactionStatus
													? "test-transaction-status-error"
													: undefined
											}
										>
											<SelectValue placeholder="Select status" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="pending">
												Pending user pickup
											</SelectItem>
											<SelectItem value="completed">Completed</SelectItem>
											<SelectItem value="failed">Failed</SelectItem>
											<SelectItem value="refunded">Refunded</SelectItem>
										</SelectContent>
									</Select>
								)}
							/>
							{errors.transactionStatus?.message && (
								<FieldError
									id="test-transaction-status-error"
									message={String(errors.transactionStatus.message)}
								/>
							)}
						</div>
					)}

					{/* Date of Birth */}
					{testCase.requiredFields.dateOfBirth && (
						<div className="space-y-1.5">
							<Label htmlFor="test-date-of-birth">
								Date of Birth Used
								<RequiredIndicator />
							</Label>
							<Controller
								control={form.control}
								name="dateOfBirth"
								render={({ field }) => (
									<DatePicker
										id="test-date-of-birth"
										value={String(field.value ?? "")}
										onChange={field.onChange}
										maxDate={new Date().toISOString().slice(0, 10)}
										className={
											errors.dateOfBirth
												? "border-(--color-error-500)"
												: undefined
										}
									/>
								)}
							/>
							{errors.dateOfBirth?.message && (
								<FieldError
									id="test-date-of-birth-error"
									message={String(errors.dateOfBirth.message)}
								/>
							)}
						</div>
					)}

					{/* Amount */}
					{testCase.requiredFields.amount && (
						<div className="space-y-1.5">
							<Label htmlFor="test-amount">
								Amount (USD/USDC)
								<RequiredIndicator />
							</Label>
							<Input
								id="test-amount"
								type="number"
								step="0.01"
								{...form.register("amount")}
								placeholder="0.00"
								className={
									errors.amount ? "border-(--color-error-500)" : undefined
								}
								aria-invalid={!!errors.amount}
								aria-describedby={
									errors.amount ? "test-amount-error" : undefined
								}
							/>
							{errors.amount?.message && (
								<FieldError
									id="test-amount-error"
									message={String(errors.amount.message)}
								/>
							)}
						</div>
					)}

					{/* Fees */}
					{testCase.requiredFields.fees && (
						<div className="space-y-1.5">
							<Label htmlFor="test-fees">
								Fees
								<RequiredIndicator />
							</Label>
							<Input
								id="test-fees"
								type="number"
								step="0.01"
								{...form.register("fees")}
								placeholder="0.00"
								className={
									errors.fees ? "border-(--color-error-500)" : undefined
								}
								aria-invalid={!!errors.fees}
								aria-describedby={errors.fees ? "test-fees-error" : undefined}
							/>
							{errors.fees?.message && (
								<FieldError
									id="test-fees-error"
									message={String(errors.fees.message)}
								/>
							)}
						</div>
					)}

					{/* Amount Received */}
					{testCase.requiredFields.amountReceived && (
						<div className="space-y-1.5">
							<Label htmlFor="test-amount-received">
								Amount Received (Local Currency)
								<RequiredIndicator />
							</Label>
							<Input
								id="test-amount-received"
								type="text"
								{...form.register("amountReceived")}
								placeholder="e.g., 24178.27 COP"
								className={
									errors.amountReceived
										? "border-(--color-error-500)"
										: undefined
								}
								aria-invalid={!!errors.amountReceived}
								aria-describedby={
									errors.amountReceived
										? "test-amount-received-error"
										: undefined
								}
							/>
							{errors.amountReceived?.message && (
								<FieldError
									id="test-amount-received-error"
									message={String(errors.amountReceived.message)}
								/>
							)}
						</div>
					)}

					{/* Transaction ID */}
					{testCase.requiredFields.transactionId && (
						<div className="space-y-1.5">
							<Label htmlFor="test-transaction-id">
								Transaction ID
								<RequiredIndicator />
							</Label>
							<Input
								id="test-transaction-id"
								type="text"
								{...form.register("transactionId")}
								placeholder="Enter transaction ID"
								className={
									errors.transactionId
										? "border-(--color-error-500)"
										: undefined
								}
								aria-invalid={!!errors.transactionId}
								aria-describedby={
									errors.transactionId ? "test-transaction-id-error" : undefined
								}
							/>
							{errors.transactionId?.message && (
								<FieldError
									id="test-transaction-id-error"
									message={String(errors.transactionId.message)}
								/>
							)}
						</div>
					)}

					{/* Screenshot Upload */}
					<div className="space-y-1.5">
						<Label>
							Screenshot
							<RequiredIndicator />
						</Label>
						<p className="mb-2 text-(--color-muted-foreground) text-[0.813rem]">
							Upload a screenshot showing the transaction details
						</p>

						<Controller
							control={form.control}
							name="screenshotUrl"
							render={({ field }) =>
								!field.value ? (
									// biome-ignore lint/a11y/noStaticElementInteractions: Drop zone needs drag handlers; label provides keyboard access
									<div
										onDrop={(e) => handleDrop(e, field.onChange)}
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-all ${isDragging ? "border-(--color-primary-500) bg-(--color-primary-50)" : "border-(--color-border) bg-(--color-muted)"}`}
									>
										<input
											type="file"
											id="test-screenshot"
											accept=".png,.jpg,.jpeg,.pdf"
											onChange={(e) => {
												const file = e.target.files?.[0];
												if (file) handleFileUpload(file, field.onChange);
											}}
											className="sr-only"
										/>
										<label
											htmlFor="test-screenshot"
											className="flex cursor-pointer flex-col items-center gap-3"
										>
											<div className="flex h-12 w-12 items-center justify-center rounded-full bg-(--color-primary-100)">
												<Upload
													size={24}
													className="text-(--color-primary-500)"
												/>
											</div>
											<div>
												<p className="mb-1 font-medium text-(--color-foreground)">
													{isDragging
														? "Drop your file here"
														: "Click to upload or drag and drop"}
												</p>
												<p className="m-0 text-(--color-muted-foreground) text-[0.813rem]">
													PNG, JPG or PDF (max. 10MB)
												</p>
												{(uploadError || errors.screenshotUrl?.message) && (
													<FieldError
														message={
															uploadError ??
															(errors.screenshotUrl?.message
																? String(errors.screenshotUrl.message)
																: "")
														}
													/>
												)}
											</div>
										</label>
									</div>
								) : (
									<div className="flex items-center justify-between rounded-lg border border-(--color-success-500) bg-(--color-success-50) p-4">
										<div className="flex items-center gap-3">
											<div className="flex h-10 w-10 items-center justify-center rounded-full bg-(--color-success-200) font-semibold text-(--color-success-500)">
												✓
											</div>
											<span className="font-medium text-(--color-success-700)">
												Screenshot uploaded
											</span>
										</div>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={() => field.onChange("")}
											aria-label="Remove screenshot"
											className="text-(--color-muted-foreground)"
										>
											<X size={20} />
										</Button>
									</div>
								)
							}
						/>
					</div>

					{/* Notes */}
					<div className="space-y-1.5">
						<Label htmlFor="test-notes">Additional Notes (Optional)</Label>
						<Textarea
							id="test-notes"
							{...form.register("notes")}
							placeholder="Any additional information or comments..."
							rows={3}
						/>
					</div>

					{/* Submit Buttons */}
					<div className="mt-5 flex justify-end gap-3">
						<Button type="button" variant="outline" onClick={onCancel}>
							Cancel
						</Button>
						<Button type="submit" disabled={submitting || isSubmitting}>
							{submitting || isSubmitting ? "Submitting…" : "Submit Results"}
						</Button>
					</div>
				</Card>
			</form>
		</div>
	);
};
